import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  FaArrowLeft,
  FaBroadcastTower,
  FaSyncAlt
} from 'react-icons/fa';

import {
  useNavigate
} from 'react-router-dom';

import {
  useTranslation
} from 'react-i18next';

import fallbackProfile from '../assets/couple.png';
import '../css/ActiveLivesPage.css';

function ActiveLivesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] =
    useState(false);
  const [error, setError] = useState('');

  const token =
    localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) {
      return null;
    }

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  const currentUser = useMemo(() => {
    try {
      const storedUser =
        localStorage.getItem('currentUser');

      return storedUser
        ? JSON.parse(storedUser)
        : null;
    } catch (parseError) {
      console.warn(
        'Unable to read current user:',
        parseError
      );

      return null;
    }
  }, []);

  const fetchActiveLives = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/live/active`,
          {
            headers: authorizationHeader
              ? {
                Authorization:
                  authorizationHeader
              }
              : {}
          }
        );

        const data = await response
          .json()
          .catch(() => []);

        if (!response.ok) {
          throw new Error(
            `Unable to load lives: ${response.status}`
          );
        }

        setLives(
          Array.isArray(data)
            ? data.filter(
              (live) =>
                String(live.status).toUpperCase() ===
                'LIVE'
            )
            : []
        );
      } catch (fetchError) {
        console.error(
          'Unable to load active lives:',
          fetchError
        );

        setError(
          t(
            'live.activeLivesError',
            'Unable to load active live sessions.'
          )
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authorizationHeader, t]
  );

  useEffect(() => {

    const handleVisibilityChange = () => {

      if (
        document.visibilityState === 'visible'
      ) {
        fetchActiveLives(true);
      }
    };


    const handleFocus = () => {
      fetchActiveLives(true);
    };


    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    window.addEventListener(
      'focus',
      handleFocus
    );


    return () => {

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      window.removeEventListener(
        'focus',
        handleFocus
      );
    };

  }, [fetchActiveLives]);

  useEffect(() => {
    fetchActiveLives();

    const interval = window.setInterval(
      () => fetchActiveLives(true),
      3000
    );

    const refreshHandler = () => {
      fetchActiveLives(true);
    };

    window.addEventListener(
      'refresh-live-sessions',
      refreshHandler
    );

    return () => {
      window.clearInterval(interval);

      window.removeEventListener(
        'refresh-live-sessions',
        refreshHandler
      );
    };
  }, [fetchActiveLives]);

  const visibleLives = lives.filter(
    (live) =>
      String(live.hostId) !==
      String(currentUser?.id)
  );

  return (
    <main className="active-lives-page">
      <header className="active-lives-header">
        <button
          type="button"
          className="active-lives-back"
          onClick={() => navigate(-1)}
          aria-label={t(
            'common.back',
            'Back'
          )}
        >
          <FaArrowLeft />
        </button>

        <div className="active-lives-heading">
          <span>
            <FaBroadcastTower />
          </span>

          <div>
            <h1>
              {t(
                'live.activeLives',
                'Live Now'
              )}
            </h1>

            <p>
              {t(
                'live.activeLivesSubtitle',
                'Join users who are currently live.'
              )}
            </p>
          </div>
        </div>

        <button
          type="button"
          className="active-lives-refresh"
          disabled={refreshing}
          onClick={() =>
            fetchActiveLives(true)
          }
          aria-label={t(
            'common.refresh',
            'Refresh'
          )}
        >
          <FaSyncAlt
            className={
              refreshing
                ? 'is-spinning'
                : ''
            }
          />
        </button>
      </header>

      {error && (
        <div
          className="active-lives-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="active-lives-state">
          <div className="active-lives-spinner" />

          <p>
            {t(
              'live.loadingLives',
              'Loading live sessions...'
            )}
          </p>
        </div>
      ) : visibleLives.length === 0 ? (
        <div className="active-lives-empty">
          <span>
            <FaBroadcastTower />
          </span>

          <h2>
            {t(
              'live.noActiveLives',
              'No one is live right now'
            )}
          </h2>

          <p>
            {t(
              'live.noActiveLivesDescription',
              'Active live sessions will appear here.'
            )}
          </p>

        </div>
      ) : (
        <div className="active-lives-grid">

          {visibleLives.map((live) => (

            <button
              key={live.id}
              type="button"
              className="active-live-bubble"
              onClick={() =>
                navigate(`/live/${live.id}`)
              }
              aria-label={t('live.joinHost', { name: live.hostName || t('navbar.user', 'User') })}
            >

              <div className="active-live-circle">

                <div className="active-live-circle-ring">

                  <div className="active-live-circle-photo">

                    <img
                      src={
                        live.hostImage ||
                        fallbackProfile
                      }
                      alt={
                        live.hostName ||
                        t(
                          'navbar.user',
                          'User'
                        )
                      }
                      onError={(event) => {
                        event.currentTarget.src =
                          fallbackProfile;
                      }}
                    />

                  </div>

                </div>


                <span className="active-live-circle-badge">
                  {t('live.badge')}
                </span>


                <span className="active-live-pulse-dot" />

              </div>


              <strong className="active-live-circle-name">

                {live.hostName ||
                  t(
                    'navbar.user',
                    'User'
                  )}

              </strong>


              <span className="active-live-circle-title">

                {live.title ||
                  t(
                    'live.liveSession',
                    'Live now'
                  )}

              </span>

            </button>

          ))}

        </div>
      )}
      {/* =================================================
    GO LIVE FLOATING BUTTON
================================================= */}

      <button
        type="button"
        className="active-lives-go-live"
        onClick={() =>
          navigate('/live/start')
        }
        aria-label={t(
          'live.goLive',
          'Go Live'
        )}
      >
        <span className="active-lives-go-live-pulse" />

        <FaBroadcastTower />

        <span>
          {t(
            'live.goLive',
            'Go Live'
          )}
        </span>
      </button>
    </main>
  );
}

export default ActiveLivesPage;
